'use client';

import { c as _c } from "react/compiler-runtime";
import type { FactiiiRequirement, FactiiiTag, PinnedFactiii } from '@shared/all/domains/factiiis';
import { type Post } from '@shared/all/domains/posts';
import { DeploymentLayout } from '@shared/all/domains/spaces';
import { calculateEditTronCostBreakdown, convertAwardToFactiiiTag, convertMyFactiiiTagsDataToFactiiiTag, convertPinnedFactiiiToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useFactiiiHandlers } from '@shared/utils/hooks/useFactiiiHandlers';
import usePinnedFactiiis from '@shared/utils/hooks/usePinnedFactiiis';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useUser from '@shared/utils/hooks/useUser';
import { type QueryKey, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useTronAffordability } from '../../hooks/useTronAffordability';
import { spaceConfig } from '../../utils/getSpaceConfig';
import { client } from '../../utils/trpc';
import { Camera } from '../common/Camera';
import Error from '../common/Error';
import { BotFactiii } from './FactiiiRenderers/BotFactiii';
import { SourceFactiii } from './FactiiiRenderers/SourceFactiii';
import { FactiiiSearch } from './FactiiiSearch';
import { FactiiiSearchResult } from './FactiiiSearchResult';
import { type SelectedAward, SelectedFactiiis } from './SelectedFactiiis';
type Props = {
  requirementsMet: {
    tag: FactiiiTag;
    results: {
      completed: boolean;
      description: string;
      requirement: FactiiiRequirement;
    }[];
  }[];
  queryKey: QueryKey;
  postId: string;
  pinnedFactiii?: PinnedFactiii;
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: Dispatch<SetStateAction<FactiiiTag[]>>;
  setNsfw: (nsfw: boolean) => void;
  setPolitics: (politics: boolean) => void;
  isPrivate?: boolean;
  isOpened: boolean;
  postFactiiisCount?: Post['postFactiiisCount'];
  postAuthorName?: string;
  // Awards "pretend to be factiiis": staged via the Awards pill and given on
  // Tag. Only wired up when tagging someone's post (postAuthorName set).
  selectedAwards?: SelectedAward[];
  setSelectedAwards?: Dispatch<SetStateAction<SelectedAward[]>>;
  // Receives the server-derived baseline (initialData) so the parent can show
  // a global Reset and gate Tag on whether the working set differs from it.
  setInitialData?: (data: FactiiiTag[]) => void;
  // When the parent renders the cost/balance summary itself (e.g. the modal
  // footer), suppress the in-body line so it doesn't eat vertical space.
  costInFooter?: boolean;
};

// The space this deployment is, when it is one space's own site rather than
// factiii.com.
const instanceSpaceSlug = spaceConfig.layoutType === DeploymentLayout.Space ? spaceConfig.space.slug : undefined;
const getDefaultTimezone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = -new Date().getTimezoneOffset() / 60;
  return {
    value: timeZone,
    label: timeZone,
    offset
  };
};
export const NewPostFactiii = props => {
  const $ = _c(59);
  const {
    addSpaceTime,
    removeSpaceTime,
    addFactiii
  } = useFactiiiHandlers();
  const {
    currentUser
  } = useUser();
  const {
    pinnedFactiiis
  } = usePinnedFactiiis(client);
  const {
    fetchPrivateMyFactiiiTags
  } = usePostsShared(client);
  const awardsEnabled = !!props.postAuthorName && !!props.setSelectedAwards;
  const [searchFilter, setSearchFilter] = useState("all");
  const [cameraOpenId, setCameraOpenId] = useState(null);
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname?.match(/^\/s\/([^/]+)/)?.[1];
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routeSpaceSlug = t0;
  const [spaceScope, setSpaceScope] = useState(routeSpaceSlug ?? instanceSpaceSlug);
  let t1;
  if ($[2] !== currentUser.id) {
    t1 = ["awards", currentUser.id];
    $[2] = currentUser.id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== awardsEnabled || $[5] !== t1) {
    t2 = {
      queryKey: t1,
      queryFn: _temp,
      staleTime: Infinity,
      enabled: awardsEnabled
    };
    $[4] = awardsEnabled;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data: awardsData
  } = useQuery(t2);
  let t3;
  if ($[7] !== props.selectedAwards) {
    t3 = new Set((props.selectedAwards ?? []).map(_temp2));
    $[7] = props.selectedAwards;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const stagedAwardIds = t3;
  let t4;
  if ($[9] !== stagedAwardIds) {
    t4 = a_0 => !stagedAwardIds.has(a_0.id);
    $[9] = stagedAwardIds;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const availableAwards = (awardsData?.awards ?? []).filter(t4);
  let t5;
  if ($[11] !== props) {
    t5 = award => {
      props.setSelectedAwards?.(prev => prev.some(a_1 => a_1.id === award.id) ? prev : [...prev, {
        id: award.id,
        title: award.tron.title,
        image: award.image,
        cost: award.tron.quantity ?? 0,
        description: award.description,
        anonymous: false,
        count: 1
      }]);
    };
    $[11] = props;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleAddAward = t5;
  let t6;
  if ($[13] !== props) {
    t6 = id => props.setSelectedAwards?.(prev_0 => prev_0.filter(a_2 => a_2.id !== id));
    $[13] = props;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleRemoveAward = t6;
  let t7;
  if ($[15] !== props) {
    t7 = (id_0, patch) => props.setSelectedAwards?.(prev_1 => prev_1.map(a_3 => a_3.id === id_0 ? {
      ...a_3,
      ...patch
    } : a_3));
    $[15] = props;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleUpdateAward = t7;
  const pills = [{
    key: "all",
    label: "All"
  }, {
    key: "sources",
    label: "Sources"
  }, {
    key: "bots",
    label: "Bots"
  }, ...(awardsEnabled ? [{
    key: "awards" as const,
    label: "Awards"
  }] : []), {
    key: "pinned",
    label: "Pinned"
  }];
  let t8;
  if ($[17] !== fetchPrivateMyFactiiiTags || $[18] !== props.isPrivate || $[19] !== props.pinnedFactiii || $[20] !== props.postId) {
    t8 = async () => {
      if (props.isPrivate) {
        return await fetchPrivateMyFactiiiTags(props.postId, props.pinnedFactiii?.id ? [props.pinnedFactiii.id] : []);
      } else {
        return unwrapFromResult(client.factiiis.myFactiiiTags.query({
          postId: props.postId,
          excludeFactiiiIds: props.pinnedFactiii?.id ? [props.pinnedFactiii.id] : []
        }));
      }
    };
    $[17] = fetchPrivateMyFactiiiTags;
    $[18] = props.isPrivate;
    $[19] = props.pinnedFactiii;
    $[20] = props.postId;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== props.queryKey || $[23] !== t8) {
    t9 = {
      queryKey: props.queryKey,
      queryFn: t8,
      refetchOnWindowFocus: false,
      gcTime: 0
    };
    $[22] = props.queryKey;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const {
    data: myFactiiiTagsData,
    isError: myFactiiiTagsError,
    isLoading: myFactiiiTagsLoading
  } = useQuery(t9);
  let t10;
  if ($[25] !== myFactiiiTagsData || $[26] !== props) {
    t10 = () => {
      if (!myFactiiiTagsData && !props.pinnedFactiii) {
        return;
      }
      const fetchedTags = (myFactiiiTagsData || []).map(_temp3);
      const pinned = props.pinnedFactiii ? [convertPinnedFactiiiToFactiiiTag(props.pinnedFactiii)] : [];
      const baseline = Array.from(new Map([...fetchedTags, ...pinned].map(_temp4)).values());
      props.setInitialData?.(baseline);
      props.setMyPostFactiiiCounts(prev_2 => {
        const byId = new Map(prev_2.map(_temp5));
        [...fetchedTags, ...pinned].forEach(t_1 => {
          if (!byId.has(t_1.factiiiId)) {
            byId.set(t_1.factiiiId, t_1);
          }
        });
        return Array.from(byId.values());
      });
    };
    $[25] = myFactiiiTagsData;
    $[26] = props;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== myFactiiiTagsData || $[29] !== props.pinnedFactiii) {
    t11 = [myFactiiiTagsData, props.pinnedFactiii];
    $[28] = myFactiiiTagsData;
    $[29] = props.pinnedFactiii;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  useEffect(t10, t11);
  let t12;
  if ($[31] !== props.isPrivate || $[32] !== props.myPostFactiiiCounts || $[33] !== props.postFactiiisCount || $[34] !== props.selectedAwards) {
    t12 = props.isPrivate ? {
      tagEditFee: 0,
      botCost: 0,
      awardCost: 0,
      total: 0
    } : calculateEditTronCostBreakdown(props.myPostFactiiiCounts, props.postFactiiisCount, (props.selectedAwards ?? []).map(_temp6));
    $[31] = props.isPrivate;
    $[32] = props.myPostFactiiiCounts;
    $[33] = props.postFactiiisCount;
    $[34] = props.selectedAwards;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const costBreakdown = t12;
  const tronCost = costBreakdown.total;
  const {
    insufficientTrons,
    poolCoversRemainder
  } = useTronAffordability(costBreakdown);
  let t13;
  if ($[36] !== props.myPostFactiiiCounts) {
    t13 = pf => !props.myPostFactiiiCounts.some(tag_0 => tag_0.factiiiId === pf.id);
    $[36] = props.myPostFactiiiCounts;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const filteredPinnedFactiiis = pinnedFactiiis.filter(t13);
  let t14;
  if ($[38] !== addSpaceTime || $[39] !== props.setMyPostFactiiiCounts) {
    t14 = (tag_1, entry) => addSpaceTime({
      tag: tag_1,
      ...entry,
      setMyPostFactiiiCounts: props.setMyPostFactiiiCounts
    });
    $[38] = addSpaceTime;
    $[39] = props.setMyPostFactiiiCounts;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const wrappedAddSpaceTime = t14;
  let t15;
  if ($[41] !== props.setMyPostFactiiiCounts || $[42] !== removeSpaceTime) {
    t15 = (tag_2, index) => removeSpaceTime({
      tag: tag_2,
      index,
      setMyPostFactiiiCounts: props.setMyPostFactiiiCounts
    });
    $[41] = props.setMyPostFactiiiCounts;
    $[42] = removeSpaceTime;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const wrappedRemoveSpaceTime = t15;
  let t16;
  if ($[44] !== addFactiii || $[45] !== props.myPostFactiiiCounts || $[46] !== props.setMyPostFactiiiCounts || $[47] !== props.setNsfw || $[48] !== props.setPolitics) {
    t16 = tag_3 => addFactiii({
      tag: tag_3,
      setMyPostFactiiiCounts: props.setMyPostFactiiiCounts,
      setPolitics: props.setPolitics,
      setNsfw: props.setNsfw,
      myPostFactiiiCounts: props.myPostFactiiiCounts
    });
    $[44] = addFactiii;
    $[45] = props.myPostFactiiiCounts;
    $[46] = props.setMyPostFactiiiCounts;
    $[47] = props.setNsfw;
    $[48] = props.setPolitics;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  const handleAddFactiii = t16;
  const t17 = "mt-2 flex flex-col";
  let t18;
  if ($[50] !== currentUser.tronsBalance || $[51] !== insufficientTrons || $[52] !== poolCoversRemainder || $[53] !== props.costInFooter || $[54] !== tronCost) {
    t18 = !props.costInFooter && tronCost > 0 && <div className={`flex items-center justify-between px-1 py-1.5 text-xs ${insufficientTrons ? "text-destructive dark:text-destructive" : "text-textSecondary"}`}><span>Cost estimate: <strong>{tronCost.toLocaleString()}</strong> trons</span><span>Balance:{" "}<strong>{currentUser.tronsBalance.toLocaleString()}</strong>{insufficientTrons && " \u2014 insufficient"}{poolCoversRemainder && <span className="text-primary">{" "}— community pool covers remainder</span>}</span></div>;
    $[50] = currentUser.tronsBalance;
    $[51] = insufficientTrons;
    $[52] = poolCoversRemainder;
    $[53] = props.costInFooter;
    $[54] = tronCost;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  const t19 = props.isOpened && <><SelectedFactiiis myPostFactiiiCounts={props.myPostFactiiiCounts} setMyPostFactiiiCounts={props.setMyPostFactiiiCounts} requirementsMet={props.requirementsMet} myFactiiiTagsData={myFactiiiTagsData} setCameraOpenId={setCameraOpenId} addSpaceTime={wrappedAddSpaceTime} removeSpaceTime={wrappedRemoveSpaceTime} defaultTimezone={getDefaultTimezone()} queryKey={props.queryKey} isPrivatePost={props.isPrivate} selectedAwards={props.selectedAwards} onRemoveAward={handleRemoveAward} onUpdateAward={handleUpdateAward} /><div className="overflow-x-hidden p-1"><div className={`mb-2 flex gap-2 ${!props.postAuthorName ? "mt-2" : ""}`}>{pills.map(pill => <button key={pill.key} className={`rounded-full px-3 py-1 text-xs font-emphasis transition-colors ${searchFilter === pill.key ? "bg-primary text-white" : "bg-background-secondary text-textSecondary hover:bg-background-light"}`} onClick={() => setSearchFilter(pill.key)}>{pill.label}</button>)}{spaceScope && searchFilter === "all" && <button className="ml-auto flex items-center gap-1 rounded-full bg-primary/15 px-3 py-1 text-xs font-emphasis text-primary transition-colors hover:bg-primary/25" onClick={() => setSpaceScope(undefined)} title="Search all of Factiii instead">s/{spaceScope}<X size={12} /></button>}</div>{searchFilter === "all" && <FactiiiSearch myPostFactiiiCounts={props.myPostFactiiiCounts} handleClick={handleAddFactiii} spaceSlug={spaceScope} />}{searchFilter === "sources" && <SourceFactiii myPostFactiiiCounts={props.myPostFactiiiCounts} setMyPostFactiiiCounts={props.setMyPostFactiiiCounts} handleClick={handleAddFactiii} />}{searchFilter === "bots" && <BotFactiii myPostFactiiiCounts={props.myPostFactiiiCounts} handleClick={handleAddFactiii} />}{searchFilter === "awards" && <div className="space-y-2"><p className="px-1 text-xs text-textSecondary">Add an award, then set how many and Public/Private on it above.</p>{availableAwards.length === 0 ? <p className="py-4 text-center text-sm text-textSecondary">{(awardsData?.awards.length ?? 0) === 0 ? "No awards available yet. Check back later!" : "All awards added."}</p> : availableAwards.map(award_0 => <FactiiiSearchResult key={award_0.id} tag={convertAwardToFactiiiTag({
          id: award_0.id,
          title: award_0.tron.title,
          image: award_0.image,
          description: award_0.description,
          cost: award_0.tron.quantity ?? 0
        })} handleClick={() => handleAddAward(award_0)} />)}</div>}{searchFilter === "pinned" && <div className="space-y-3">{filteredPinnedFactiiis.length === 0 ? <p className="py-4 text-center text-sm text-textSecondary">{pinnedFactiiis.length === 0 ? "No pinned factiiis yet. Pin some factiiis to quickly access them here." : "No pinned factiiis match your search."}</p> : filteredPinnedFactiiis.map(pinnedFactiii => {
          const factiiiTag = convertPinnedFactiiiToFactiiiTag(pinnedFactiii);
          return <FactiiiSearchResult key={pinnedFactiii.id} tag={factiiiTag} handleClick={() => handleAddFactiii(factiiiTag)} />;
        })}</div>}</div><Error loading={myFactiiiTagsLoading} error={myFactiiiTagsError} />{cameraOpenId && <Camera closeHandler={() => setCameraOpenId(null)} setNewPhotoFcn={photo => {
      const cameraTag = props.myPostFactiiiCounts.find(tag_4 => tag_4.factiiiId === cameraOpenId);
      if (!cameraTag) {
        return toast("No Camera Tag Found", "error");
      }
      const updatedTag = {
        ...cameraTag,
        postFactiiis: [{
          ...cameraTag.postFactiiis[0],
          photo
        }, ...cameraTag.postFactiiis.slice(1)]
      };
      const newPostFactiiiCounts = props.myPostFactiiiCounts.map(tag_5 => tag_5.id === cameraTag.id ? updatedTag : tag_5);
      props.setMyPostFactiiiCounts(newPostFactiiiCounts);
      setCameraOpenId(null);
    }} />}</>;
  let t20;
  if ($[56] !== t18 || $[57] !== t19) {
    t20 = <div className={t17}>{t18}{t19}</div>;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  return t20;
};
function _temp() {
  return unwrapFromResult(client.awards.getItems.query());
}
function _temp2(a) {
  return a.id;
}
function _temp3(tag) {
  return convertMyFactiiiTagsDataToFactiiiTag(tag);
}
function _temp4(t) {
  return [t.factiiiId, t];
}
function _temp5(t_0) {
  return [t_0.factiiiId, t_0];
}
function _temp6(a_4) {
  return {
    cost: a_4.cost,
    count: a_4.count
  };
}